import {
  GroupColumnHeadings,
  GroupColumns,
  GridRowGroups,
} from "../grid/GroupHeadings";
import { Fragment } from "react";
import { VisualGroupHeading } from "../VisualGroupHeading";
import { groupHeading } from "../visualGroups";
import { Box } from "@mui/material";
import { ActionButton } from "../../components/ui";
import MultiNumericInput from "../../components/ui/MultiNumericInput";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import { GridColumnHeaderText } from "../grid/GridColumnHeaderText";
import { GridRowContent } from "../grid/GridRowContent";
import {
  compactGridCardStyle,
  compactGridInputStyle,
  compactGridListStyle,
  optionalGridLineStyle,
  wideGridCellStyle,
  wideGridColumnHeaderStyle,
  wideGridInstrumentStyle,
  wideGridRowHeaderStyle,
  wideGridTableStyle,
  wideGridViewportStyle,
} from "../grid/gridInstrumentStyle";
import { portraitRowHeader } from "../grid/portraitStyles";
import {
  gridNumericTotals,
  type GridNumericDefinition,
  type GridNumericDraft,
  type GridNumericEvent,
} from "./core";
import { gridTotalValueText, totalText } from "./styles";

type GridNumericViewProps = QuestionViewProps<
  GridNumericDefinition,
  GridNumericDraft,
  GridNumericEvent
>;

export const GridNumericView = (props: GridNumericViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const { definition, draft } = props;
  const compact = props.display.arrangement === "compact";
  const hasRowGroups = Boolean(definition.rowGroups?.length);
  const grouped = hasRowGroups || Boolean(definition.columnGroups?.length);
  const totals =
    definition.autosumRows || definition.autosumColumns
      ? gridNumericTotals(definition, draft)
      : [];
  const rowContent = (row: GridNumericDefinition["rows"][number]) => (
    <GridRowContent
      row={row}
      compact={compact}
      specify={props.controls.specify}
    />
  );
  const rowIsDontKnow = (rowId: string) =>
    definition.dontKnow !== undefined &&
    draft.valuesByRow[rowId]?.length === definition.columns.length &&
    draft.valuesByRow[rowId].every(
      (value) => value === definition.dontKnow?.value,
    );

  const dontKnowControl = (
    row: GridNumericDefinition["rows"][number],
    rowIndex: number,
  ) => (
    <ActionButton
      currentVal={{
        text: definition.dontKnow!.label,
        display_text: true,
      }}
      isSelected={rowIsDontKnow(row.id)}
      ariaPressed={rowIsDontKnow(row.id)}
      ariaLabel={`${row.label}: ${definition.dontKnow!.label}`}
      ariaDisabled={!props.controls.canSubmit}
      onClick={() =>
        props.dispatch({ type: "toggle-dont-know", rowId: row.id })
      }
      testId={`grid-numeric-dont-know-${rowIndex}`}
    />
  );

  const numericInput = (
    row: GridNumericDefinition["rows"][number],
    rowIndex: number,
    columnIndex: number,
  ) => (
    <MultiNumericInput
      rowIndex={rowIndex}
      colIndex={columnIndex}
      value={
        rowIsDontKnow(row.id)
          ? ""
          : (draft.valuesByRow[row.id]?.[columnIndex] ?? "")
      }
      ariaLabel={`${draft.specifiedRows?.[row.id] || row.label}: ${definition.columns[columnIndex] || theme.copy.labels.number}`}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          props.submit();
        }
      }}
      onChange={(event) =>
        props.dispatch({
          type: "set-cell",
          rowId: row.id,
          columnIndex,
          value: event.target.value,
        })
      }
    />
  );

  const cell = (
    row: GridNumericDefinition["rows"][number],
    rowIndex: number,
    columnIndex: number,
  ) => (
    <Box key={columnIndex} sx={compactGridInputStyle(theme)}>
      {numericInput(row, rowIndex, columnIndex)}
    </Box>
  );

  const total = (index: number) => (
    <Box
      data-testid={`grid-numeric-total-${index}`}
      sx={gridTotalValueText(theme, totals[index].complete)}
    >
      {totals[index].value}
    </Box>
  );

  return (
    <Box
      data-testid="grid-numeric-view"
      data-grid-ready="true"
      sx={{
        width: "100%",
        ...(compact
          ? {}
          : wideGridInstrumentStyle(theme, theme.show_grid_lines, grouped)),
      }}
    >
      {compact ? (
        <Box sx={compactGridListStyle(theme)}>
          {definition.rows.map((row, rowIndex) => (
            <Fragment key={row.id}>
              <VisualGroupHeading
                text={groupHeading(definition.rowGroups, row.id)}
              />
              <Box sx={compactGridCardStyle(theme)}>
                <Box sx={portraitRowHeader}>{rowContent(row)}</Box>
                {definition.columns.map((column, columnIndex) => (
                  <Box key={columnIndex}>
                    <VisualGroupHeading
                      text={groupHeading(definition.columnGroups, column)}
                    />
                    {column && <GridColumnHeaderText text={column} />}
                    {cell(row, rowIndex, columnIndex)}
                  </Box>
                ))}
                {definition.autosumRows && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box sx={totalText(theme)}>{theme.copy.labels.total}</Box>
                    {total(rowIndex)}
                  </Box>
                )}
                {definition.dontKnow && (
                  <Box sx={{ alignSelf: "flex-end" }}>
                    {dontKnowControl(row, rowIndex)}
                  </Box>
                )}
              </Box>
            </Fragment>
          ))}
          {definition.autosumColumns && (
            <Box sx={compactGridCardStyle(theme)}>
              <Box sx={totalText(theme)}>{theme.copy.labels.total}</Box>
              {definition.columns.map((column, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <GridColumnHeaderText text={column} />
                  {total(index)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={wideGridViewportStyle}>
          <Box component="table" sx={wideGridTableStyle}>
            <colgroup>
              <col style={{ width: "30%" }} />
            </colgroup>
            <GroupColumns
              values={definition.columns}
              groups={definition.columnGroups}
            />
            {definition.autosumRows && (
              <colgroup>
                <col style={{ width: "10%" }} />
              </colgroup>
            )}
            {definition.dontKnow && (
              <colgroup>
                <col style={{ width: "22%" }} />
              </colgroup>
            )}
            <Box component="thead">
              <GroupColumnHeadings
                values={definition.columns}
                groups={definition.columnGroups}
                trailingColumns={
                  Number(definition.autosumRows) +
                  Number(Boolean(definition.dontKnow))
                }
              />
              <Box component="tr">
                <Box
                  component="th"
                  scope="col"
                  sx={wideGridRowHeaderStyle(theme)}
                />
                {definition.columns.map((column, index) => (
                  <Box
                    component="th"
                    scope="col"
                    key={index}
                    sx={wideGridColumnHeaderStyle(theme)}
                  >
                    <GridColumnHeaderText text={column} />
                  </Box>
                ))}
                {definition.autosumRows && (
                  <Box
                    component="th"
                    scope="col"
                    sx={wideGridColumnHeaderStyle(theme)}
                  >
                    {theme.copy.labels.total}
                  </Box>
                )}
                {definition.dontKnow && (
                  <Box
                    component="th"
                    scope="col"
                    sx={wideGridColumnHeaderStyle(theme)}
                  />
                )}
              </Box>
            </Box>
            <GridRowGroups
              rows={definition.rows}
              groups={definition.rowGroups}
              rowId={(row) => row.id}
              columns={
                1 +
                definition.columns.length +
                Number(definition.autosumRows) +
                Number(Boolean(definition.dontKnow))
              }
            >
              {(row, rowIndex) => (
                <Box component="tr">
                  <Box
                    component="th"
                    scope="row"
                    sx={wideGridRowHeaderStyle(theme)}
                  >
                    {rowContent(row)}
                  </Box>
                  {definition.columns.map((_, columnIndex) => (
                    <Box
                      component="td"
                      key={columnIndex}
                      sx={wideGridCellStyle(theme)}
                    >
                      {numericInput(row, rowIndex, columnIndex)}
                    </Box>
                  ))}
                  {definition.autosumRows && (
                    <Box
                      component="td"
                      sx={{
                        textAlign: "center",
                        ...optionalGridLineStyle(theme),
                      }}
                    >
                      {total(rowIndex)}
                    </Box>
                  )}
                  {definition.dontKnow && (
                    <Box
                      component="td"
                      sx={{
                        textAlign: "center",
                        ...optionalGridLineStyle(theme),
                      }}
                    >
                      {dontKnowControl(row, rowIndex)}
                    </Box>
                  )}
                </Box>
              )}
            </GridRowGroups>
            {definition.autosumColumns && (
              <Box component="tfoot">
                <Box component="tr">
                  <Box
                    component="th"
                    scope="row"
                    sx={wideGridRowHeaderStyle(theme)}
                  >
                    {theme.copy.labels.total}
                  </Box>
                  {definition.columns.map((_, index) => (
                    <Box
                      component="td"
                      key={index}
                      sx={{
                        textAlign: "center",
                        ...optionalGridLineStyle(theme),
                      }}
                    >
                      {total(index)}
                    </Box>
                  ))}
                  {definition.dontKnow && (
                    <Box component="td" sx={optionalGridLineStyle(theme)} />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
